import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { Person } from 'models/person';
import { Topic } from 'models/topic';
import { ServicesContext } from 'services/context';
import { DateHelpers } from '../../../helpers/date';
import DiscussionManager from '../../../managers/discussion';
import Discussion from '../../../models/discussion';
import './removeButton.html';
import CommentRequestButtonVM, { CommentRequestButtonVMConfig } from './requestButton';

@injectable()
export default class CommentRemoveButtonVM extends CommentRequestButtonVM {
    private user: Subscribable<Person | undefined>
    private discussion: Subscribable<Discussion | undefined>
    private topic: Subscribable<Topic | undefined>

    private isRankingStage: Subscribable<boolean>
    private isVotingStage: Subscribable<boolean>

    private canRemove: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: CommentRequestButtonVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.servicesContext) private ctx: ServicesContext
    ) {
        super(config, info);

        const topicId = this.comment.pluck(c => c.topicId);

        const userManager = storage.get(CurrentUserManager, { });
        this.user = userManager.pluck('person');

        const discussionManager = storage.get(DiscussionManager, {});
        this.discussion = discussionManager.pluck(m => m.discussion);

        const topicManager = storage.get(TopicManager, { topicId });
        this.topic = topicManager.pluck(m => m.topic);

        const ideationManager = storage.get(IdeationManager, { topicId });
        this.isRankingStage = ideationManager.pluck(m => m.isRatingStage, false);
        this.isVotingStage = ideationManager.pluck(m => m.isVotingStage, false);

        this.canRemove = ko.flattenComputed(() => this.checkIfCanBeRemoved());
    }

    remove = () => {
        if (this.isLocked()) return;

        const comment = this.comment();

        if (comment != undefined) {
            system.confirm(messages.AreYouSureYouWantToDeleteThisComment, () => system.lockOrSkip(this.isBusy, async () => {
                try {
                    await this.ctx.commentsService.remove(comment);
                } catch (ex) {
                    system.handleError(ex, true);
                }
            }));
        }
    }

    protected override checkIfVisible() {
        return super.checkIfVisible() && this.canRemove();
    }

    private checkIfCanBeRemoved() {
        const user = this.user()
        const discussion = this.discussion();
        const topic = this.topic();
        const comment = this.comment();

        if (discussion == undefined || topic == undefined || user == undefined || comment == undefined)
            return false;

        if (comment.IsModeratorIdea())
            return false;
        
        const createDate = comment.createDate();

        if (createDate == undefined)
            return false;        

        if (this.isRankingStage()) {
            //we don't allow to delete ideas in ranking stage
            if (comment.parentId() == undefined)
                return false;

            //you can change your vote only during voting
            if (comment.isIdeationVote())
                return this.isVotingStage();
        }

        if (user.isModerator()) {
            return !discussion.isHomework();
        }

        if (comment.canBeDeleted()) {
            const discussionDeleteTimeout = discussion.deleteCommentTimeout();
            const topicDeleteTimeout = topic.deleteCommentTimeout();

            const deleteTimeout = topicDeleteTimeout == -2 ? discussionDeleteTimeout : topicDeleteTimeout;

            switch (deleteTimeout) {
                case -1: return true;
                case 0: return false;
                default: return ko.timeCondition({ before: DateHelpers.add(createDate, { minutes: deleteTimeout }) });
            }
        }

        return false;
    }
}